/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppButton from "components/AppButton";
import CmnPageLoading from "components/CmnPageLoading";
import { ReactComponent as Retry } from "assets/nav_icon_reload.svg";
import { ReactComponent as Caution } from "features/wc/assets/caution.svg";
import AppScreen from "components/AppScreen";
import Header from "components/Header";
import CrowdLabel from "features/wc/components/CrowdLabel"
import Colors from "util/colors";
import { AdjustmentWcInfoList, AdjustmentWcInfoDetail } from "features/wc/types";
import { BUILDING_ID } from "config";
import UsageDialog from "../components/UsageDialog";
import useWcInfo from "../hooks/useWcInfo";

const colors = Colors.get();

// スタイル設定
const styles = {
  // 使い方
  usage: css({
    width: "50px",
    minWidth: "50px",
    height: "20px",
    borderRadius: "0px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: colors.main,
    padding: "0",
    boxShadow: "none",
    fontSize: "12px",
    color: colors.main,
  }),
  // 再試行
  retry: css({
    width: "149px",
    minWidth: "50px",
    height: "44px",
    borderRadius: "6px",
    padding: "0",
    boxShadow: "none",
    fontSize: "15px",
    color: colors.white,
    backgroundColor: colors.main,
  }),
  // ローディング
  loadingPosition: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
}
const Content = styled("div")({
  width: "100vw",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "0 13px 0 13px"
});
const TitleBar = styled("div")({
  width: "100%",
  maxWidth:"580px",
  display: "flex",
  justifyContent: "right",
  padding: "10px 0 10px 0",
  margin: "30px 0 10px 0",
});
const Title = styled("div")({
  height: "40px",
  width:"100%",
  maxWidth:"580px",
  textAlign: "center",
  fontSize: "24px",
  lineHeight: "36px",
  fontWeight: 600,
  color: colors.main,
});
const UsageBar = styled("div")({
  height: "30px",
  width: "100%",
  display: "flex",
  justifyContent: "flex-end",
  textAlign: "right",
  alignItems: "center",
  color: colors.main,
});
const WcType = styled("div")({
  display: "flex",
  lineHeight: "24px",
  alignItems: "center",
});
const WcTypeText = styled("div")({
  fontSize: "16px",
  fontWeight: 600,
  color: colors.main,
  marginLeft: "6px",
});
const Info = styled("div")({
  width: "100%",
  maxWidth:"580px",
});
const Row = styled("div")({
  marginBottom: "20px"
});
const RetryInfo = styled("div")({
  width: "100%",
  height: "80vh",
  maxWidth:"580px",
  display: "grid",
  placeContent: "center",
  placeItems: "center"
});
const RetryRow = styled("div")({
  marginBottom: "30px"
});
const RetryButtonText = styled("div")({
  marginLeft: "10px",
});
const RetryText = styled("div")({
  marginBottom: "30px",
  fontSize: "15px",
  fontWeight: 600,
  whiteSpace: "pre",
  textAlign: "center",
});
const RetryBar = styled("div")({
});
const AnnotationBar = styled("div")({
  fontSize: "10px",
  fontWeight: 600,
  lineHeight: "11px",
  marginBottom: "20px"
});

/**
 * 満空情報表示画面
 */
export default function WcCrowd() {
  const { t } = useTranslation("トイレ可視化");
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isApp = params.get("isApp") || "";
  const [{wcInfoList, isLoading},{get:getWcInfo}] = useWcInfo();

  const [wcList, setWcList] = useState<AdjustmentWcInfoList | null>(null);
  const [isOpendDialog, setIsOpendDialog] = useState(false);

  useEffect(() => {
    getWcInfo(BUILDING_ID);
  }, []);

  /**
   * 情報取得時はソート、グルーピングを行う
   */
  useEffect(() => {
    if(!wcInfoList) {
      return;
    }

    // ソート
    const wcArray = Array.from(wcInfoList);
    wcArray.sort((a,b) => {
      if(a.type_disp_order < b.type_disp_order) return -1;
      if(a.type_disp_order > b.type_disp_order) return 1;
      if(a.location_disp_order < b.location_disp_order) return -1;
      if(a.location_disp_order > b.location_disp_order) return 1;
      return 0;
    });

    // グルーピング
    const newWcList: AdjustmentWcInfoList = [];
    for (let i = 0; i < wcArray.length; i += 1) {
      let added = false;
      for (let j = 0; j < newWcList.length; j += 1) {
        if(wcArray[i].type === newWcList[j].type){
          newWcList[j].detail.push({
            location_name: wcArray[i].location_name,
            location_disp_order: wcArray[i].location_disp_order,
            booth_count: wcArray[i].booth_count,
            open_booth_count: wcArray[i].open_booth_count
          })
          added = true;
          break;
        }
      }
      if(!added){
        const adjustmentWcInfoDetail:AdjustmentWcInfoDetail[] = [{
          location_name: wcArray[i].location_name,
          location_disp_order: wcArray[i].location_disp_order,
          booth_count: wcArray[i].booth_count,
          open_booth_count: wcArray[i].open_booth_count
        }]
        newWcList.push({
          type: wcArray[i].type,
          icon: wcArray[i].icon,
          description: wcArray[i].description,
          type_disp_order: wcArray[i].type_disp_order,
          detail: adjustmentWcInfoDetail
        })
      }
    }

    setWcList(newWcList);
  },[wcInfoList]) 

  /**
   * 戻るボタン押下時処理
   */
  const onClickBack = () => {
    window.history.back();
  };

  /**
   * 更新ボタン押下時処理
   */
  const onClickReload = () => {
    getWcInfo(BUILDING_ID);
  };

  /**
   * 再試行ボタン押下時処理
   */
  const onClickRetry = () => {
    getWcInfo(BUILDING_ID);
  };

  /**
   * 使い方ボタン押下時処理
   */
  const onClickUsage = () => {
    setIsOpendDialog(true);
  };

  /**
   * 使い方ポップアップを閉じた時
   */
  const handleCloseUsageDialog = () => {
    setIsOpendDialog(false);
  };

  if(isLoading){
    return (<CmnPageLoading
    header={isApp && (<Header 
      title={t("ホームタイトル")}
      onClickBack={onClickBack}
      onClickReload={onClickReload}/>)}  />);
  }

  return (
    <AppScreen resetScroll={false} bgColor>
      {isApp && (<Header
        title={t("ホームタイトル")}
        onClickBack={onClickBack}
        onClickReload={onClickReload}
       />)}
      <Content>
        {wcList && 
          (!isApp && 
            <TitleBar>
              <Title>{t("ホームタイトル")}</Title>
            </TitleBar>
          )}
        {wcList && 
          (wcList.length === 0 ? 
            <RetryInfo>
              <RetryRow>
                <Caution />
              </RetryRow>
              <RetryText>
                {t("取得失敗")}
              </RetryText>
              <RetryBar>
                <AppButton
                  onClick={onClickRetry}
                  id="RETRY"
                  sx={styles.retry}
                  >
                  <Retry width="18px" height="18px" />
                  <RetryButtonText>
                    {t("再試行")}
                  </RetryButtonText>
                </AppButton>
              </RetryBar>
            </RetryInfo> 
          : 
            <Info>
              <UsageBar>
                <AppButton
                  onClick={onClickUsage}
                  id="USAGE"
                  sx={styles.usage}
                  >
                  {t("使い方ボタン")}
                </AppButton>
              </UsageBar>
              {wcList.map((wcData) => (
                <Row key={wcData.type}>
                  <WcType>
                    <img src={`data:image/svg+xml;utf8,${encodeURIComponent(wcData.icon)}`} alt="icon"/>
                    <WcTypeText>{wcData.type}</WcTypeText>
                  </WcType>
                  {wcData.detail.map((wcDetail) => (
                    <CrowdLabel
                      key={wcDetail.location_name}
                      locationName={wcDetail.location_name}
                      openBoothCount={wcDetail.open_booth_count}
                      boothCount={wcDetail.booth_count}
                    />
                  ))}
                </Row>
              ))}
              <AnnotationBar>
                {t("注釈")}
              </AnnotationBar>
            </Info>
          )
        }

        <UsageDialog 
          isOpen={isOpendDialog}
          adjustmentWcInfoList={wcList}
          onClose={handleCloseUsageDialog}
        />
      </Content>
    </AppScreen>
  );
}
